import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { exchangeRealtimeCookie } from '@/services/realtime';
import { BASE_URL } from '@core/constants';

import { TRANSCRIPTION_TASKS_QUERY_KEY } from './useTranscriptionTasks';

const STORAGE_KEY = 'activeTranscriptionTasks';
const NON_TERMINAL = ['pending', 'processing', 'retry'];
const TERMINAL = ['completed', 'failed', 'revoked'];

function loadFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return {};
    const record = {};
    raw.forEach(task => {
      if (task?.taskId) record[task.taskId] = task;
    });
    return record;
  } catch {
    return {};
  }
}

/**
 * Hook-efecto dueño exclusivo de las conexiones SSE y de la persistencia a
 * localStorage. Se monta UNA sola vez en App.jsx (vía <TranscriptionTasksSync />
 * más abajo) -- reemplaza el polling que antes vivía duplicado en
 * GenerateReport.jsx y ActiveTasksBadge.jsx.
 *
 * No expone estado: los consumidores leen con los hooks de
 * useTranscriptionTasks.js (useActiveTranscriptionTasks / useTranscriptionTaskForStudy).
 */
export function useTranscriptionTasksSync() {
  const queryClient = useQueryClient();
  const connectionsRef = useRef(new Map()); // taskId -> EventSource (o null mientras se autentica)

  useEffect(() => {
    let cancelled = false;

    function handleMessage(taskId, event) {
      if (!event?.data) return;
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      queryClient.setQueryData(TRANSCRIPTION_TASKS_QUERY_KEY, prev => {
        if (!prev || !prev[taskId]) return prev;
        return { ...prev, [taskId]: { ...prev[taskId], ...data } };
      });

      if (TERMINAL.includes(data.status)) {
        connectionsRef.current.get(taskId)?.close();
        connectionsRef.current.delete(taskId);

        // Delay breve para que la UI (badge/editor) alcance a mostrar
        // el estado final antes de que la tarea desaparezca del registro.
        setTimeout(() => {
          queryClient.setQueryData(TRANSCRIPTION_TASKS_QUERY_KEY, prev => {
            if (!prev || !(taskId in prev)) return prev;
            const { [taskId]: _removed, ...rest } = prev;
            return rest;
          });
        }, 1500);
      }
    }

    async function connectSSE(taskId) {
      if (connectionsRef.current.has(taskId)) return; // dedupe
      // Reserva sincrónica: como exchangeRealtimeCookie() es async, dos
      // llamadas a connectSSE(taskId) casi simultáneas (ej. la hidratación
      // inicial y el primer evento del cache) pasarían el dedupe de arriba
      // antes de que ninguna haya hecho set() todavía. Reservamos el slot
      // YA, antes del await, para que la segunda llamada sí vea el dedupe.
      connectionsRef.current.set(taskId, null);

      // EventSource nativo no manda headers -- autenticamos con la misma
      // cookie firmada httpOnly que usa citamed247/realtime (rt_sid),
      // reusando el mismo endpoint de intercambio. `?token=` en la URL
      // queda como fallback (igual que openTestStream) por si el navegador
      // bloquea cookies third-party.
      try {
        await exchangeRealtimeCookie();
      } catch (err) {
        console.warn('[SSE transcripcion] no se pudo intercambiar cookie de auth:', err);
      }
      if (cancelled) {
        connectionsRef.current.delete(taskId);
        return;
      }

      const token = localStorage.getItem('token') || '';
      const qs = token ? `?token=${encodeURIComponent(token)}` : '';
      const es = new EventSource(
        `${BASE_URL}insurance/transcribe/task-status-stream/${taskId}/${qs}`,
        { withCredentials: true }
      );
      connectionsRef.current.set(taskId, es);

      // Deliberado: a diferencia de usePresencePing/useBulkPresence (que sí
      // pausan en document.hidden), acá cortar la conexión en background
      // perdería el evento "completed" de una transcripción de varios
      // minutos -- justo el bug que se busca eliminar. EventSource nativo
      // no se pausa solo por pestaña oculta, no hace falta nada especial.
      es.onmessage = event => handleMessage(taskId, event);
      es.addEventListener('close', () => {
        es.close();
        connectionsRef.current.delete(taskId);
      });
      es.onerror = err => {
        // No cerramos: EventSource reintenta solo con backoff nativo del
        // navegador mientras readyState no sea CLOSED.
        console.warn('[SSE transcripcion] error, reintentando:', err);
      };
    }

    // Hidratación inicial desde localStorage (recuperación tras recarga).
    const initialRecord = loadFromStorage();
    if (Object.keys(initialRecord).length > 0) {
      queryClient.setQueryData(TRANSCRIPTION_TASKS_QUERY_KEY, initialRecord);
    }

    // Único punto de escritura a localStorage: se deriva del cache cada vez
    // que cambia, y conecta SSE para cualquier tarea nueva sin conexión
    // activa (ej. startTrackingTranscriptionTask escribiendo una tarea nueva).
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (event?.query?.queryKey?.[0] !== TRANSCRIPTION_TASKS_QUERY_KEY[0]) return;

      const record = queryClient.getQueryData(TRANSCRIPTION_TASKS_QUERY_KEY) || {};
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.values(record)));

      Object.values(record).forEach(task => {
        if (task?.taskId && NON_TERMINAL.includes(task.status)) {
          connectSSE(task.taskId);
        }
      });
    });

    // Conectar de inmediato las tareas recuperadas de localStorage (el
    // subscribe de arriba solo reacciona a cambios FUTUROS del cache, el
    // setQueryData de hidratación ya disparó uno, pero por si el cache
    // estaba vacío y no hubo cambio, forzamos un primer chequeo).
    Object.values(initialRecord).forEach(task => {
      if (task?.taskId && NON_TERMINAL.includes(task.status)) {
        connectSSE(task.taskId);
      }
    });

    const connections = connectionsRef.current;
    return () => {
      cancelled = true;
      unsubscribe();
      connections.forEach(es => es?.close());
      connections.clear();
    };
  }, [queryClient]);
}

/** Componente renderless para montar el hook en App.jsx junto a ActiveTasksBadge. */
export function TranscriptionTasksSync() {
  useTranscriptionTasksSync();
  return null;
}
